/**
 * Client-side scope filtering for WebSocket-pushed items.
 *
 * `/ws/drone` is a single broadcast channel with no per-group routing — every
 * connected client receives every group's `incident_alert`. The list endpoints
 * are scoped (`incident_routes.list_incidents`), so a fetch is always correct,
 * but a push is not: after switching groups the previous group's incidents keep
 * arriving and repaint themselves onto the map until the operator reloads.
 *
 * `belongsToScope` mirrors the server predicate exactly:
 *   group    -> group_id = <active group>
 *   personal -> created_by = <user> AND group_id IS NULL
 */

export type DataScope = 'group' | 'personal';

export interface ScopeIdentity {
  scope: DataScope;
  groupId: number | null;
  userId: number | null;
}

export interface ScopedItem {
  group_id?: number | null;
  created_by?: number | null;
}

export function belongsToScope(item: ScopedItem, identity: ScopeIdentity): boolean {
  // A payload that carries no scope fields cannot be judged. Admit it rather
  // than drop it — dropping would turn any trimmed broadcast into silently
  // missing alerts, which is a worse failure than the one being fixed.
  if (item.group_id === undefined && item.created_by === undefined) return true;

  if (identity.scope === 'personal') {
    if (item.group_id != null) return false;
    if (item.created_by == null || identity.userId == null) return true;
    return item.created_by === identity.userId;
  }

  if (item.group_id === undefined) return true;
  if (identity.groupId == null) return false;
  return item.group_id === identity.groupId;
}
